import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface PrivacyPreferences {
  analytics: boolean;
  marketing: boolean;
  necessary: boolean;
  preferences: boolean;
  lastUpdated: Date;
  bannerShown: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PrivacyService {
  private readonly STORAGE_KEY = 'privacy_preferences';
  private preferences = new BehaviorSubject<PrivacyPreferences>(this.getDefaultPreferences());

  constructor() {
    this.loadPreferences();
  }

  private getDefaultPreferences(): PrivacyPreferences {
    return {
      analytics: false,
      marketing: false,
      necessary: true, // Toujours activé car nécessaire au fonctionnement
      preferences: false,
      lastUpdated: new Date(),
      bannerShown: false
    };
  }

  private loadPreferences(): void {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      try {
        const preferences = JSON.parse(stored);
        preferences.lastUpdated = new Date(preferences.lastUpdated);
        this.preferences.next(preferences);
      } catch {
        this.preferences.next(this.getDefaultPreferences());
      }
    }
  }

  getPreferences(): Observable<PrivacyPreferences> {
    return this.preferences.asObservable();
  }

  shouldShowBanner(): boolean {
    const prefs = this.preferences.value;
    return !prefs.bannerShown;
  }

  updatePreferences(preferences: Partial<PrivacyPreferences>): void {
    const current = this.preferences.value;
    const updated = {
      ...current,
      ...preferences,
      lastUpdated: new Date(),
      bannerShown: true // Marquer la bannière comme affichée
    };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updated));
    this.preferences.next(updated);
  }

  resetPreferences(): void {
    const defaultPrefs = this.getDefaultPreferences();
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(defaultPrefs));
    this.preferences.next(defaultPrefs);
  }

  exportUserData(): string {
    // À implémenter selon les données que vous stockez
    const userData = {
      preferences: this.preferences.value,
      // Ajouter d'autres données utilisateur ici
    };
    return JSON.stringify(userData, null, 2);
  }

  deleteUserData(): void {
    // Supprimer toutes les données utilisateur stockées localement
    localStorage.removeItem(this.STORAGE_KEY);
    // Ajouter d'autres suppressions de données ici
    this.preferences.next(this.getDefaultPreferences());
  }
} 